import React, { useEffect, useRef, useState } from 'react'
import { ScrollingNumber } from 'react-scrolling-currency';

/**
 * ScrollingNumber measures its digit box height once per digit change and then
 * scrolls each column by (boxHeight * digit). It never remeasures, so any layout
 * change after that measurement leaves every column parked between two digits —
 * the amount renders with staggered digits, or as unreadable slices.
 *
 * On a first, uncached visit that is exactly what happens: the ticker measures
 * the fallback font's metrics, then the Poppins webfont swaps in and the box
 * grows. Gating on document.fonts.ready does NOT help — the Google Fonts
 * stylesheet is a <link>, so its @font-face rules are not registered yet and
 * fonts.ready resolves (with document.fonts.size === 0) before the font exists.
 *
 * Instead, watch the box for a real height change and remount the ticker so it
 * remeasures against the current metrics. This is self-correcting whatever moved
 * the layout: font swap, late CSS, or a responsive breakpoint.
 */
const AmountDisplay = ({ amount }) => {
  const wrapperRef = useRef(null);
  const measuredHeight = useRef(null);
  const [measureKey, setMeasureKey] = useState(0);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(() => {
      const box = wrapper.querySelector('.tickerColumnContainer');
      const height = box ? box.clientHeight : 0;
      if (!height) return;

      // First callback records the height the ticker just measured itself against.
      if (measuredHeight.current === null) {
        measuredHeight.current = height;
        return;
      }
      if (height !== measuredHeight.current) {
        measuredHeight.current = height;
        setMeasureKey((key) => key + 1);
      }
    });

    observer.observe(wrapper);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={wrapperRef} style={{ textAlign: 'center', lineHeight: 'normal' }}>
      <ScrollingNumber
        key={measureKey}
        number={Number(amount)}
        isCurrency={true}
        locale="en-US"
        currencyCode="USD"
      />
    </div>
  )
}

export default AmountDisplay
